import { observable, action, computed } from "mobx";

import * as jwtDecode from "jwt-decode";

import { AuthEndpoints } from "../endpoints/AuthEndpoints";

export enum AccessLevel {
    ADMIN = "ADMIN"
}

export interface Session {
    userId: number;
    username: string;
    firstName: string;
    lastName: string;
    email: string;
    accessLevel: AccessLevel;
}

interface SessionToken extends Session {
    exp: number;
}

const LOCAL_STORAGE_SESSION_KEY = "lsys.session";
const Now = () => new Date().getTime() / 1000;

export class SessionStore {
    @observable private _loggedIn: boolean = false;
    @observable private _latestToken: string | null = null;
    @observable private _flashMessage: string = "";
    @observable private _session: Session | null = null;

    constructor(private authEndpoints: AuthEndpoints, public onLoggedIn: () => void = () => true, public onLoggedOut: () => void = () => true) {
        this.onLoggedIn = onLoggedIn;
        this.onLoggedOut = onLoggedOut;
    }

    @action onLoad() {
        this.readLocalStorage();
    }

    @action async login(username: string, password: string): Promise<Session> {
        try {
            const token = await this.authEndpoints.getToken({ username, password });
            this.parseJwt(token.token, true);

            if (this._session === null) {
                throw new Error("Could not parse session token");
            }
            return this._session;
        } catch (e) {
            this.resetState();
            throw e;
        }
    }

    @action async updateToken(tokenString: string): Promise<Session> {
        this.parseJwt(tokenString, false);
        if (this._session === null) {
            throw new Error("Could not parse session token");
        }
        return this._session;
    }

    @action async changePassword(oldPassword: string, newPassword: string, confirmPassword: string): Promise<Session> {
        const token = await this.authEndpoints.changePassword({ oldPassword, newPassword, confirmPassword });
        this.parseJwt(token.token, false);
        if (this._session === null) {
            throw new Error("Could not parse session token");
        }
        return this._session;
    }

    @action async logout(flashMessage: string = "") {
        this.resetState();
        this._flashMessage = flashMessage;

        this.clearLocalStorage();
        this.onLoggedOut();
    }

    @computed get loggedIn(): boolean {
        return this._loggedIn;
    }

    @computed get flashMessage(): string {
        return this._flashMessage;
    }

    @computed get tokenString(): string | null {
        return this._latestToken;
    }

    @computed get authorizationString(): string {
        return this.tokenString ? `Bearer ${this.tokenString}` : "";
    }

    @computed get fullName(): string {
        return this.session ? `${this.session.firstName} ${this.session.lastName}` : "";
    }

    @computed get session(): Session | null {
        return this._session;
    }

    private parseJwt(tokenString: string, notifyLogin: boolean = false) {
        this._latestToken = tokenString;
        const token = jwtDecode<SessionToken>(tokenString);
        if (token && token.exp - Now() > 0) {
            this._loggedIn = true;
            this._session = JSON.parse(JSON.stringify(token));
            this.updateLocalStorage();
            if (notifyLogin) {
                this.onLoggedIn();
            }
        } else {
            this.resetState();
        }
    }

    private resetState() {
        this._loggedIn = false;
        this._latestToken = null;
        this._flashMessage = "";
        this._session = null;
    }

    private updateLocalStorage() {
        localStorage.setItem(LOCAL_STORAGE_SESSION_KEY, `${this._latestToken}`);
    }

    private clearLocalStorage() {
        localStorage.removeItem(LOCAL_STORAGE_SESSION_KEY);
    }

    private readLocalStorage() {
        const token = localStorage.getItem(LOCAL_STORAGE_SESSION_KEY);
        if (token) {
            this.parseJwt(token, true);
        }
    }
}



// WEBPACK FOOTER //
// ./src/stores/SessionStore.ts